<template>
  <Layout>
    <div v-if="showConfetti" class="confetti-overlay">
      <Vue3Lottie :animationData="confettiAnimation" :autoPlay="true" :loop="false" @onComplete="showConfetti = false" />
    </div>
    <div class="addon-page">
      <div class="addon-page-header">
        <div class="back-btn" @click="$router.back()">
          <img class="back-icon" src="@/assets/images/arrowLeft.svg" />
        </div>
        <div>
          <h2 class="addon-page-title">{{ $t('addon_page_title') }}</h2>
          <p class="addon-page-subtitle">{{ $t('addon_page_subtitle') }}</p>
        </div>
      </div>

      <!-- Demo pakette ek ürün satın alınamaz -->
      <div v-if="isDemoPackage" class="addon-unavailable">
        <div class="addon-unavailable-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="22" stroke="#E5E7EB" stroke-width="2"/>
            <path d="M24 16V26M24 32H24.02" stroke="#98A2B3" stroke-width="2.5" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="addon-unavailable-title">{{ $t('addon_not_available_title') }}</h3>
        <p class="addon-unavailable-text">{{ $t('addon_not_available_demo') }}</p>
        <button class="upgrade-btn" @click="$router.push('/billing')">{{ $t('upgrade_package') }}</button>
      </div>

      <div v-else class="addon-container">
        <!-- Left: Addon Products -->
        <div class="addon-products-card">
          <!-- AI Credit Info Banner -->
          <div v-if="aiCreditData && aiCreditData.hasAccess" class="ai-credit-banner">
            <div class="ai-credit-banner-header">
              <div class="ai-credit-banner-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 2L12.5 7.5L18 8.5L14 12.5L15 18L10 15.5L5 18L6 12.5L2 8.5L7.5 7.5L10 2Z" fill="#F59E0B" stroke="#F59E0B" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="ai-credit-banner-info">
                <span class="ai-credit-banner-title">{{ $t('ai_credit_title') }}</span>
                <span class="ai-credit-banner-subtitle">
                  {{ $t('ai_credit_remaining_info', { remaining: aiCreditData.remaining, total: aiCreditData.total }) }}
                </span>
              </div>
            </div>
            <div class="ai-credit-banner-bar">
              <div class="progress-bar">
                <div
                    class="progress-fill"
                    :style="{ width: aiCreditUsagePercentage + '%' }"
                    :class="{
                      'progress-danger': aiCreditUsagePercentage > 90,
                      'progress-warning': aiCreditUsagePercentage > 70 && aiCreditUsagePercentage <= 90,
                      'progress-success': aiCreditUsagePercentage <= 70
                    }"
                ></div>
              </div>
            </div>
          </div>

          <div class="addon-products-header">
            <img src="@/assets/images/addos.svg" class="addon-products-icon" />
            <h3>{{ $t('addon_packages') }}</h3>
          </div>

          <div v-if="isLoading" class="addon-loading">
            <span>{{ $t('loading') }}...</span>
          </div>

          <div v-else-if="addonProducts.length === 0" class="addon-empty">
            <p>{{ $t('no_addon_products') }}</p>
          </div>

          <div v-else class="addon-products-list">
            <div
              v-for="product in addonProducts"
              :key="product.productKey"
              class="addon-product-item"
            >
              <div class="addon-product-info">
                <span class="addon-product-name">
                  {{ product.displayName }}
                  <svg class="info-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" stroke="#98A2B3" stroke-width="1.2"/>
                    <path d="M7 6.5V9.5" stroke="#98A2B3" stroke-width="1.2" stroke-linecap="round"/>
                    <circle cx="7" cy="4.75" r="0.75" fill="#98A2B3"/>
                  </svg>
                </span>
                <span class="addon-product-price">
                  {{ $t('price') }}: ${{ product.unitPrice.toFixed(2) }} / {{ product.unit }}
                  <span class="addon-product-period">
                    ({{ periodLabel(product.billingPeriod) }})
                  </span>
                </span>
                <span v-if="product.billingPeriod === 'monthly'" class="addon-product-yearly-hint">
                  {{ $t('addon_monthly_yearly_total', { yearly: (product.unitPrice * 12).toFixed(2) }) }}
                </span>
                <span v-else-if="product.billingPeriod === 'one-time'" class="addon-product-onetime-hint">
                  {{ $t('addon_onetime_hint') }}
                </span>
              </div>
              <div class="addon-quantity-control">
                <button class="quantity-btn" @click="decreaseQuantity(product.productKey)" :disabled="getQuantity(product.productKey) === 0">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 7H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
                <span class="quantity-value">{{ getQuantity(product.productKey) }}</span>
                <button class="quantity-btn" @click="increaseQuantity(product.productKey)">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 3V11M3 7H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Cart Summary -->
        <div class="cart-card">
          <div class="cart-header">
            <img src="@/assets/images/basket.svg" class="cart-icon" />
            <h3>{{ $t('cart_summary') }}</h3>
          </div>

          <div class="cart-content">
            <div class="cart-section">
              <span class="cart-section-title">{{ $t('added_packages') }}:</span>
              <div v-if="cartItems.length === 0" class="cart-empty">
                {{ $t('no_packages_added') }}
              </div>
              <div v-else class="cart-items">
                <div v-for="item in displayCartItems" :key="item.productKey" class="cart-item-block">
                  <div class="cart-item">
                    <span class="cart-item-name">{{ item.displayName }} x{{ item.quantity }}</span>
                    <span class="cart-item-price">${{ item.lineTotal.toFixed(2) }}</span>
                  </div>
                  <div v-if="item.fullLineTotal && item.fullLineTotal > item.lineTotal" class="cart-item-full">
                    <span class="cart-item-full-label">{{ $t('addon_list_price') }}:</span>
                    <span class="cart-item-full-price">${{ item.fullLineTotal.toFixed(2) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bilgi notu: parent paketle aynı tarihte sona erer -->
            <div v-if="prorationInfo && cartItems.length > 0" class="proration-note">
              <img src="/src/assets/images/info.svg" />
              <div>
                <strong>{{ $t('addon_proration_title') }}</strong>
                <p v-if="isFullPeriod">
                  {{ $t('addon_full_period_text', {
                    endDate: formatDate(prorationInfo.parentEndDate)
                  }) }}
                </p>
                <p v-else>
                  {{ $t('addon_proration_text', {
                    days: prorationInfo.remainingDays,
                    endDate: formatDate(prorationInfo.parentEndDate)
                  }) }}
                </p>
              </div>
            </div>

            <div class="cart-divider" />

            <div class="cart-section">
              <span class="cart-section-title">{{ $t('transaction_summary') }}</span>
              <div class="cart-summary-row">
                <span>{{ $t('subtotal') }}</span>
                <span>${{ subtotal.toFixed(2) }}</span>
              </div>
              <div class="cart-summary-row">
                <span>{{ $t('taxes') }} ({{ vatRate }}%)</span>
                <span>${{ taxes.toFixed(2) }}</span>
              </div>
              <div v-if="discount > 0" class="cart-summary-row">
                <span>{{ $t('discount') }}</span>
                <span class="discount-value">-${{ discount.toFixed(2) }}</span>
              </div>
            </div>

            <div class="cart-divider" />

            <div class="cart-total">
              <span>{{ $t('total_amount') }}:</span>
              <span class="cart-total-price">
                <span v-if="previewLoading" class="cart-total-loading">…</span>
                <span v-else>${{ totalAmount.toFixed(2) }}</span>
              </span>
            </div>

            <div class="cart-info">
              <img src="/src/assets/images/info.svg">
              <span>{{ $t('addon_info_text') }}</span>
            </div>

            <button class="checkout-btn" :disabled="cartItems.length === 0 || previewLoading" :class="{ disabled: cartItems.length === 0 || previewLoading }" @click="showCheckoutModal = true">
              {{ $t('proceed_to_payment') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <AddonCheckoutModal
      :isVisible="showCheckoutModal"
      :cartItems="cartItems"
      :subtotal="subtotal"
      :taxes="taxes"
      :discount="discount"
      :totalAmount="totalAmount"
      @close="showCheckoutModal = false"
      @payment-success="onPaymentSuccess"
    />
  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import AddonCheckoutModal from "@/components/modals/AddonCheckoutModal.vue";
import {Vue3Lottie} from "vue3-lottie";
import confettiData from '@/assets/animations/confetti.json';
import pricingService from "@/service/pricingService";
import {handleErrorResponse} from "@/utils/utils";

export default {
  name: 'AddonPackages',
  components: { Layout, AddonCheckoutModal, Vue3Lottie },

  data() {
    return {
      isLoading: false,
      isDemoPackage: false,
      addonProducts: [],
      quantities: {},
      showCheckoutModal: false,
      showConfetti: false,
      confettiAnimation: confettiData,

      // AI Credit state
      aiCreditData: null,

      // Pro-rata preview state
      previewLoading: false,
      previewItems: [],          // server'dan dönen item array'i (pro-rated fiyatlarla)
      previewSubtotal: 0,
      previewVatRate: 20,
      previewVatAmount: 0,
      previewTotal: 0,
      prorationInfo: null,       // { remainingDays, parentTotalDays, ratio, parentEndDate }
      _previewDebounceTimer: null,
    };
  },

  computed: {
    aiCreditUsagePercentage() {
      if (!this.aiCreditData || !this.aiCreditData.total) return 0;
      return Math.round((this.aiCreditData.used / this.aiCreditData.total) * 100);
    },

    /**
     * Lokal sepet (pro-rata yok, sadece kullanıcı seçimini takip etmek için).
     * Modal'a ve "boş mu" kontrolüne bunu kullanıyoruz.
     */
    cartItems() {
      return this.addonProducts
        .filter(p => this.getQuantity(p.productKey) > 0)
        .map(p => ({
          productId: p.productId,
          productKey: p.productKey,
          displayName: p.displayName,
          quantity: this.getQuantity(p.productKey),
          unitPrice: p.unitPrice,
          total: this.getQuantity(p.productKey) * p.unitPrice
        }));
    },

    /**
     * UI'da gösterilecek sepet item'ları — önce preview'dan, yoksa lokal'den.
     */
    displayCartItems() {
      if (this.previewItems && this.previewItems.length > 0) {
        return this.previewItems.map(it => ({
          productKey: it.productKey,
          displayName: typeof it.productName === 'object'
            ? (it.productName[this.$i18n.locale] || it.productName.tr || it.productName.en || it.productKey)
            : (it.productName || it.productKey),
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          fullUnitPrice: it.fullUnitPrice,
          lineTotal: it.lineTotal,
          fullLineTotal: it.fullLineTotal,
        }));
      }
      // Preview yoksa lokal görünüm (henüz çağrılmamış)
      return this.cartItems.map(it => ({
        ...it,
        lineTotal: it.total,
        fullLineTotal: it.total,
      }));
    },

    /**
     * Preview API başarılı dönerse onu kullan, yoksa lokal hesap (fallback).
     * Lokal hesap pro-rata olmadan tam fiyatı gösterir.
     */
    hasPreview() {
      return this.previewItems && this.previewItems.length > 0;
    },
    /**
     * Parent paket bugün başladıysa veya kalan = toplam ise pro-rata yok demek.
     * Mesaj farklı gösterilmeli (kafa karışıklığı önleme).
     */
    isFullPeriod() {
      if (!this.prorationInfo) return false;
      return Number(this.prorationInfo.ratio) >= 0.999;
    },
    localSubtotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    },
    subtotal() {
      return this.hasPreview ? this.previewSubtotal : this.localSubtotal;
    },
    taxes() {
      return this.hasPreview
        ? this.previewVatAmount
        : Math.round(this.localSubtotal * 0.20 * 100) / 100;
    },
    vatRate() { return this.previewVatRate; },
    discount() { return 0; },
    totalAmount() {
      return this.hasPreview ? this.previewTotal : (this.localSubtotal + this.taxes);
    },
  },

  watch: {
    quantities: {
      handler() { this.schedulePreview(); },
      deep: true,
    },
  },

  mounted() {
    this.loadAddonProducts();
    this.loadAiCreditData();
  },

  methods: {
    getQuantity(productKey) { return this.quantities[productKey] || 0; },
    increaseQuantity(productKey) {
      this.quantities = { ...this.quantities, [productKey]: this.getQuantity(productKey) + 1 };
    },
    decreaseQuantity(productKey) {
      const current = this.getQuantity(productKey);
      if (current > 0) { this.quantities = { ...this.quantities, [productKey]: current - 1 }; }
    },

    async loadAddonProducts() {
      try {
        this.isLoading = true;
        const response = await pricingService.getPackages();
        if (response.data.responseType === 'success') {
          const packages = response.data.data || [];
          const packageStatus = response.data.packageStatus;

          // Demo paket kontrolü
          if (packageStatus?.isDemo === true || packageStatus?.type === 'demo') {
            this.isDemoPackage = true;
            return;
          }

          const activePackage = packages.find(pkg => pkg.name === packageStatus?.packageName) || packages[0];
          const addonFeatures = activePackage?.addonFeatures || [];
          const allAddonProducts = addonFeatures.flatMap(category => category.products || []);
          this.addonProducts = allAddonProducts
            .filter(product => product.isChargeable)
            // productKey alfabetik — Pricing/CompanyDetails ile tutarlı
            .sort((a, b) => (a.productKey || '').localeCompare(b.productKey || ''))
            .map(product => ({
              productId: product.productId,
              productKey: product.productKey,
              displayName: product.productName?.trim() || product.productKey,
              unitPrice: parseFloat(product.price) || 0,
              currency: product.currency || 'USD',
              // Ürünün satış period'u (yearly/monthly). Backend resource'dan geliyor.
              billingPeriod: product.billingPeriod || 'yearly',
              // unit artık paket büyüklüğünü de içeriyor (ör. "50 GB", "100 Dakika", "1 Adet")
              unit: (product.displayText && product.displayText.trim())
                  || `${product.limitValue || ''} ${this.getUnitLabel(product.limitType)}`.trim()
                  || this.getUnitLabel(product.limitType),
              limitType: product.limitType,
              limitValue: product.limitValue,
              description: product.productDescription || ''
            }));
        }
      } catch (error) { handleErrorResponse(error, this.$snackbar); }
      finally { this.isLoading = false; }
    },

    getUnitLabel(limitType) {
      const units = { 'count': this.$t('unit_user'), 'GB': 'GB', 'MB': 'MB', 'minute': this.$t('unit_minute'), 'hour': this.$t('unit_hour'), 'isCheck': '' };
      return units[limitType] || this.$t('unit_piece');
    },

    /**
     * Period etiketi (yearly/monthly/one-time → çevrilmiş etiket)
     */
    periodLabel(billingPeriod) {
      switch (billingPeriod) {
        case 'monthly':  return this.$t('addon_monthly_label');
        case 'one-time': return this.$t('addon_onetime_label');
        case 'yearly':
        default:         return this.$t('addon_yearly_label');
      }
    },

    async loadAiCreditData() {
      try {
        const packagesResponse = await pricingService.getPackages();
        const companyUuid = packagesResponse.data?.packageStatus?.companyUuid
          || JSON.parse(localStorage.getItem('user') || '{}')?.companyUuid;

        if (!companyUuid) return;

        const response = await pricingService.getAiCreditLimit(companyUuid);
        if (response.data?.hasAccess !== undefined) {
          this.aiCreditData = response.data;
        } else if (response.data?.data) {
          this.aiCreditData = response.data.data;
        }
      } catch (e) {
        this.aiCreditData = null;
      }
    },

    onPaymentSuccess() {
      this.showCheckoutModal = false;
      this.showConfetti = true;
      setTimeout(() => {
        this.$router.push('/billing');
      }, 2000);
    },

    /**
     * Quantity değişiminde debounce ile preview API'sini tetikler.
     */
    schedulePreview() {
      if (this._previewDebounceTimer) {
        clearTimeout(this._previewDebounceTimer);
      }
      this._previewDebounceTimer = setTimeout(() => this.fetchPreview(), 300);
    },

    /**
     * previewAddonPurchase çağırıp pro-rata fiyatı server'dan alır.
     */
    async fetchPreview() {
      const items = this.addonProducts
        .filter(p => this.getQuantity(p.productKey) > 0)
        .map(p => ({
          productId: p.productId,  // UUID veya numeric — backend her ikisini de kabul eder
          quantity: this.getQuantity(p.productKey),
        }))
        .filter(i => i.productId);

      if (items.length === 0) {
        this.resetPreview();
        return;
      }

      try {
        this.previewLoading = true;
        const response = await pricingService.previewAddonPurchase(items);
        if (response.data.responseType === 'success' && response.data.data) {
          const data = response.data.data;
          this.previewItems    = data.items || [];
          this.previewSubtotal = parseFloat(data.subtotal) || 0;
          this.previewVatRate  = data.vatRate || 20;
          this.previewVatAmount = parseFloat(data.vatAmount) || 0;
          this.previewTotal    = parseFloat(data.totalAmount) || 0;
          this.prorationInfo   = data.proration || null;
        }
      } catch (error) {
        // Sessizce başarısız ol — listede pro-rata gösterilmez
        this.resetPreview();
      } finally {
        this.previewLoading = false;
      }
    },

    resetPreview() {
      this.previewItems     = [];
      this.previewSubtotal  = 0;
      this.previewVatAmount = 0;
      this.previewTotal     = 0;
      this.prorationInfo    = null;
    },

    formatDate(isoDate) {
      if (!isoDate) return '';
      try {
        const d = new Date(isoDate);
        return d.toLocaleDateString(this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US', {
          year: 'numeric', month: 'long', day: 'numeric'
        });
      } catch (e) { return isoDate; }
    },
  }
};
</script>

<style scoped>
.confetti-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999; pointer-events: none;
}
.addon-page { width: 100%; }
.addon-page-header { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 32px; }
.back-btn {
  background-color: #F2F4F7; border: none; cursor: pointer;
  border-radius: 3000px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 4px;
}
.back-btn:hover { background-color: #E4E7EC; }
.back-icon { padding: 8px; }
.addon-page-title { font-size: 22px; font-family: euclid_semi_bold, sans-serif; color: #101828; margin: 0; }
.addon-page-subtitle { font-size: 14px; font-family: euclid_regular, sans-serif; color: #667085; margin: 4px 0 0 0; }
.addon-container { display: flex; gap: 24px; align-items: flex-start; }

/* Left Column */
.addon-products-card { flex: 1; background: white; border-radius: 12px; border: 1px solid #E5E7EB; padding: 24px; }
.addon-products-header { display: flex; align-items: center; gap: 10px; margin-bottom: 24px; }
.addon-products-header h3 { font-size: 18px; font-family: euclid_semi_bold, sans-serif; color: #101828; margin: 0; }
.addon-products-icon { width: 20px; height: 20px; }
.addon-products-list { display: flex; flex-direction: column; }
.addon-product-item { display: flex; align-items: center; justify-content: space-between; padding: 20px 0; border-bottom: 1px solid #F2F4F7; }
.addon-product-item:last-child { border-bottom: none; }
.addon-product-info { display: flex; flex-direction: column; gap: 4px; }
.addon-product-name { font-size: 14px; font-family: euclid_semi_bold, sans-serif; color: #101828; display: flex; align-items: center; gap: 6px; }
.info-icon { cursor: pointer; }
.addon-product-price { font-size: 12px; font-family: euclid_regular, sans-serif; color: #667085; }
.addon-quantity-control { display: flex; align-items: center; gap: 16px; }
.quantity-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 1px solid #D0D5DD;
  background: white; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #344054; transition: all 0.2s ease;
}
.quantity-btn:hover:not(:disabled) { border-color: #344054; background-color: #F9FAFB; }
.quantity-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.quantity-value { font-size: 16px; font-family: euclid_medium, sans-serif; color: #101828; min-width: 32px; text-align: center; }

/* Right Column - Cart */
.cart-card { width: 380px; background: white; border-radius: 12px; border: 1px solid #E5E7EB; padding: 24px; position: sticky; top: 100px; }
.cart-header { display: flex; align-items: center; gap: 10px; margin-bottom: 24px; }
.cart-header h3 { font-size: 18px; font-family: euclid_semi_bold, sans-serif; color: #101828; margin: 0; }
.cart-icon { width: 20px; height: 20px; }
.cart-content { display: flex; flex-direction: column; gap: 16px; }
.cart-section-title { font-size: 14px; font-family: euclid_semi_bold, sans-serif; color: #101828; display: block; margin-bottom: 8px; }
.cart-empty { font-size: 13px; font-family: euclid_regular, sans-serif; color: #98A2B3; padding: 8px 0; }
.cart-items { display: flex; flex-direction: column; gap: 12px; }
.cart-item-block { display: flex; flex-direction: column; gap: 2px; }
.cart-item { display: flex; justify-content: space-between; align-items: center; }
.cart-item-name { font-size: 13px; font-family: euclid_regular, sans-serif; color: #344054; }
.cart-item-price { font-size: 13px; font-family: euclid_medium, sans-serif; color: #101828; }
.cart-item-full { display: flex; justify-content: space-between; align-items: center; }
.cart-item-full-label { font-size: 11px; font-family: euclid_regular, sans-serif; color: #98A2B3; }
.cart-item-full-price { font-size: 11px; font-family: euclid_regular, sans-serif; color: #98A2B3; text-decoration: line-through; }
.proration-note {
  display: flex; gap: 10px; padding: 12px;
  background-color: #EFF8FF; border-radius: 8px;
  border: 1px solid #B2DDFF;
}
.proration-note img { flex-shrink: 0; margin-top: 2px; width: 16px; height: 16px; }
.proration-note strong {
  display: block; font-size: 12px; font-family: euclid_semi_bold, sans-serif;
  color: #175CD3; margin-bottom: 4px;
}
.proration-note p {
  margin: 0; font-size: 11px; font-family: euclid_regular, sans-serif;
  color: #1849A9; line-height: 1.5;
}
.cart-total-loading { color: #98A2B3; }
.addon-product-period { font-size: 11px; color: #98A2B3; margin-left: 4px; }
.addon-product-yearly-hint {
  display: block;
  font-size: 10px;
  color: #4A68F8;
  font-family: euclid_regular, sans-serif;
  margin-top: 2px;
}
.addon-product-onetime-hint {
  display: block;
  font-size: 10px;
  color: #B54708;
  font-family: euclid_regular, sans-serif;
  margin-top: 2px;
}
.cart-divider { height: 1px; background-color: #F2F4F7; }
.cart-summary-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
.cart-summary-row span { font-size: 13px; font-family: euclid_regular, sans-serif; color: #344054; }
.discount-value { color: #10B981 !important; }
.cart-total { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px 0; }
.cart-total span:first-child { font-size: 14px; font-family: euclid_medium, sans-serif; color: #344054; }
.cart-total-price { font-size: 28px; font-family: euclid_semi_bold, sans-serif; color: #101828; }
.cart-info { display: flex; align-items: flex-start; gap: 8px; padding: 12px; background-color: #F9FAFB; border-radius: 8px; }
.cart-info svg { flex-shrink: 0; margin-top: 2px; }
.cart-info span { font-size: 11px; font-family: euclid_regular, sans-serif; color: #667085; line-height: 1.5; }
.checkout-btn {
  width: 100%; padding: 14px; border-radius: 8px; border: none;
  background-color: #101828; color: white; font-size: 14px;
  font-family: euclid_medium, sans-serif; cursor: pointer; transition: all 0.2s ease;
}
.checkout-btn:hover:not(.disabled) { background-color: #1D2939; }
.checkout-btn.disabled { background-color: #E5E7EB; color: #98A2B3; cursor: not-allowed; }
.addon-loading, .addon-empty { text-align: center; padding: 40px; color: #667085; font-family: euclid_regular, sans-serif; font-size: 14px; }

/* Demo / Unavailable State */
.addon-unavailable {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 80px 24px;
  background: white; border-radius: 12px; border: 1px solid #E5E7EB;
}
.addon-unavailable-icon { margin-bottom: 24px; }
.addon-unavailable-title {
  font-size: 18px; font-family: euclid_semi_bold, sans-serif;
  color: #101828; margin: 0 0 8px 0;
}
.addon-unavailable-text {
  font-size: 14px; font-family: euclid_regular, sans-serif;
  color: #667085; margin: 0 0 24px 0; max-width: 400px; line-height: 1.5;
}
.upgrade-btn {
  padding: 12px 32px; border-radius: 8px; border: none;
  background-color: #101828; color: white; font-size: 14px;
  font-family: euclid_medium, sans-serif; cursor: pointer; transition: all 0.2s ease;
}
.upgrade-btn:hover { background-color: #1D2939; }

/* AI Credit Banner */
.ai-credit-banner {
  padding: 16px;
  background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
  border: 1px solid #FDE68A;
  border-radius: 10px;
  margin-bottom: 0;
}
.ai-credit-banner-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.ai-credit-banner-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.ai-credit-banner-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ai-credit-banner-title {
  font-size: 14px;
  font-family: euclid_semi_bold, sans-serif;
  color: #92400E;
}
.ai-credit-banner-subtitle {
  font-size: 12px;
  font-family: euclid_regular, sans-serif;
  color: #B45309;
}
.ai-credit-banner-bar .progress-bar {
  height: 6px;
  background: rgba(255,255,255,0.6);
}
.progress-success { background: #10b981 !important; }
.progress-warning { background: #f59e0b !important; }
.progress-danger { background: #ef4444 !important; }

@media (max-width: 768px) {
  .addon-container { flex-direction: column; }
  .cart-card { width: 100%; position: static; }
}
</style>
